import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import styled from 'styled-components'
import { useQuery } from 'react-query'
import { motion } from 'framer-motion'
import { LoadingOutlined } from '@ant-design/icons'

import {
  WelcomeAnswer,
  ThankYouAnswer,
  ProductRecommendationAnswer,
  StatementAnswer,
  TextAnswer,
  EmailAnswer,
  LinkAnswer,
  NumberAnswer,
  RatingAnswer,
  OpinionAnswer,
  DateAnswer,
  YesNoAnswer,
  DiscountCodeAnswer,
  PhoneNumberAnswer,
  MultipleChoiceAnswer,
  DropdownAnswer,
  PictureChoiceAnswer,
  WalletAnswer,
  FormFieldSubmitButton,
  SkipButton,
} from '@prehook/shared'

import { sortArray } from '@/utils'
import { useLocalStorage } from '../../hooks'

const SubmitWrapper = styled(motion.div)`
  margin-top: 30px;
  margin-bottom: 50px;
`
const SubmitPlaceHolder = styled.div`
  margin-top: 30px;
  margin-bottom: 50px;
  height: 45px;
`

const renderAnswer = ({
  subId,
  formField,
  theme,
  formFieldOptionIndex = [],
  setValid,
  answer,
  handleAnswer,
  ffShortId,
  shortId,
  draft,
  storeCurrency,
  domain,
  sendIntegrationData,
  sendGaEvent,
  quizName,
  history,
}) => {
  const { fieldType, settings, formFieldOptions, title } = formField
  const sortedFFOptions =
    formFieldOptionIndex?.length > 0
      ? sortArray(formFieldOptions, formFieldOptionIndex)
      : formFieldOptions

  switch (fieldType) {
    case 'WELCOME':
      return <WelcomeAnswer />
    case 'THANK_YOU':
      return <ThankYouAnswer />
    case 'STATEMENT':
      return <StatementAnswer />
    case 'PRODUCT_RECOMMENDATION':
      const [submissions] = useLocalStorage('sub', {})
      const { isLoading, data } = useQuery('recommendedProducts', async () => {
        const url = `${process.env.GATSBY_API_URL}/recommendation`
        const sub = submissions?.[shortId]
        const ffId = formField.id
        const params = {
          shortId,
          history,
          ffId,
        }
        if (sub) {
          params.sub = sub
        } else {
          // if someone is using incognito to complete the quiz, localstorage potentially does not exist
          params.incognito = 1
          params.subId = subId
        }
        if (draft) {
          params.draft = 1
        }

        const res = await axios.post(url, params)

        return res.data
      })

      if (isLoading) {
        return <LoadingOutlined />
      }

      const handleAddToCart = (productId, quantity = 1) => {
        if (!draft) {
          const data = JSON.stringify({ type: 'addToCart', productId, quantity })
          window.top.postMessage(data, '*')
        }
      }

      const handleCartRedirect = () => {
        if (!draft) {
          const data = JSON.stringify({ type: 'cartRedirect', domain })
          window.top.postMessage(data, '*')
          sendGaEvent({
            action: 'click',
            category: 'proceedToCart',
            label: quizName,
          })
        }
      }

      return (
        <ProductRecommendationAnswer
          draft={draft}
          color={theme.answerTextColor}
          buttonColor={theme.buttonColor}
          buttonTextColor={theme.buttonTextColor}
          font={theme.font}
          settings={settings}
          products={data?.data.recommendations}
          groupedProducts={data?.data.groupedRecommendations}
          usingFallback={data?.data.usingFallback}
          storeCurrency={storeCurrency}
          domain={domain}
          handleAddToCart={handleAddToCart}
          handleCartRedirect={handleCartRedirect}
          handleIntegrationData={sendIntegrationData}
          sendGaEvent={sendGaEvent}
        />
      )
    case 'TEXT':
      return (
        <TextAnswer
          color={theme.answerTextColor}
          font={theme.font}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          setValid={setValid}
          ffShortId={ffShortId}
        />
      )
    case 'EMAIL':
      return (
        <EmailAnswer
          color={theme.answerTextColor}
          font={theme.font}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          setValid={setValid}
          ffShortId={ffShortId}
        />
      )
    case 'LINK':
      return (
        <LinkAnswer
          color={theme.answerTextColor}
          font={theme.font}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          setValid={setValid}
          ffShortId={ffShortId}
        />
      )
    case 'NUMBER':
      return (
        <NumberAnswer
          color={theme.answerTextColor}
          font={theme.font}
          settings={settings}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          setValid={setValid}
          ffShortId={ffShortId}
        />
      )
    case 'RATING':
      return (
        <RatingAnswer
          answerTextColor={theme.answerTextColor}
          buttonColor={theme.buttonColor}
          buttonTextColor={theme.buttonTextColor}
          steps={settings?.steps}
          iconType={settings?.iconType}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          ffShortId={ffShortId}
        />
      )
    case 'OPINION':
      return (
        <OpinionAnswer
          answerTextColor={theme.answerTextColor}
          font={theme.font}
          settings={settings}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          ffShortId={ffShortId}
        />
      )
    case 'DATE':
      return (
        <DateAnswer
          color={theme.answerTextColor}
          font={theme.font}
          dateDivider={settings?.dateDivider}
          dateFormat={settings?.dateFormat}
          setValid={setValid}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          ffShortId={ffShortId}
        />
      )
    case 'YES_NO':
      return (
        <YesNoAnswer
          answerTextColor={theme.answerTextColor}
          answerBackgroundColor={theme.answerBackgroundColor}
          borderRadius={theme.borderRadius}
          lineWeight={theme.lineWeight}
          font={theme.font}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          ffShortId={ffShortId}
        />
      )
    case 'DISCOUNT_CODE':
      return (
        <DiscountCodeAnswer
          color={theme.answerTextColor}
          font={theme.font}
          discountCode={settings?.discountCode}
        />
      )
    case 'PHONE_NUMBER':
      return (
        <PhoneNumberAnswer
          font={theme.font}
          answerTextColor={theme.answerTextColor}
          questionColor={theme.questionColor}
          defaultCountry={settings?.defaultCountry}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          setValid={setValid}
          ffShortId={ffShortId}
          settings={settings}
        />
      )
    case 'MULTIPLE_CHOICE':
      return (
        <MultipleChoiceAnswer
          answerTextColor={theme.answerTextColor}
          answerBackgroundColor={theme.answerBackgroundColor}
          borderRadius={theme.borderRadius}
          lineWeight={theme.lineWeight}
          font={theme.font}
          multipleSelection={settings?.multipleSelection}
          sortedFFOptions={sortedFFOptions}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          ffShortId={ffShortId}
          sendGaEvent={sendGaEvent}
        />
      )
    case 'DROPDOWN':
      return (
        <DropdownAnswer
          color={theme.answerTextColor}
          buttonColor={theme.buttonColor}
          buttonTextColor={theme.buttonTextColor}
          bgColor={theme.backgroundColor}
          font={theme.font}
          sortedFFOptions={sortedFFOptions}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          ffShortId={ffShortId}
        />
      )
    case 'PICTURE':
      return (
        <PictureChoiceAnswer
          answerTextColor={theme.answerTextColor}
          answerBackgroundColor={theme.answerBackgroundColor}
          font={theme.font}
          lineWeight={theme.lineWeight}
          multipleSelection={settings?.multipleSelection}
          sortedFFOptions={sortedFFOptions}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          imageLocation={{
            imageDomain: process.env.GATSBY_IMAGE_DOMAIN,
            bucketName: process.env.GATSBY_S3_BUCKET_NAME,
          }}
          ffShortId={ffShortId}
          settings={settings}
        />
      )
    case 'WALLET':
      return (
        <WalletAnswer
          color={theme.answerTextColor}
          font={theme.font}
          answer={answer}
          title={title}
          handleAnswer={handleAnswer}
          setValid={setValid}
          ffShortId={ffShortId}
          settings={settings}
        />
      )
    default:
      return <WelcomeAnswer />
  }
}

const Answer = ({
  subId,
  formField,
  theme,
  handleSubmit,
  handleAnswer,
  setValid,
  valid,
  setAllowNext,
  answer,
  subAnswer,
  formFieldOptionIndex,
  shortId,
  draft,
  storeCurrency,
  domain,
  sendIntegrationData,
  sendGaEvent,
  quizName,
  history,
}) => {
  useEffect(() => {
    const requiredCheck = showSubmit(subAnswer)
    let allowNext = true

    if (
      formField.fieldType === 'PRODUCT_RECOMMENDATION' ||
      formField.fieldType === 'THANK_YOU'
    ) {
      allowNext = false
    } else if (formField.required) {
      allowNext = requiredCheck && valid
    }

    setAllowNext(allowNext)
  }, [subAnswer, valid])

  const { settings, fieldType } = formField
  const ffShortId = formField.id.slice(-5)
  const buttonText = settings?.buttonText || 'Submit'
  const buttonLink = settings?.buttonLink
  const targetBlank = settings?.targetBlank
  const autoRedirect = settings?.autoRedirect
  const buttonActive = settings?.buttonActive

  // Auto-redirect is implemented as a mount effect inside
  // FormFieldSubmitButton (shared), so it never fired when a thank-you step
  // hides its button — checkEnding() returns false and the button component
  // never mounts. Fire it here for exactly that case, mirroring the
  // button's URL normalization and target semantics; when the button IS
  // shown, its own effect handles the redirect and this no-ops.
  useEffect(() => {
    if (
      fieldType === 'THANK_YOU' &&
      autoRedirect &&
      buttonLink &&
      buttonActive !== true
    ) {
      const regex = /^((http|https):\/\/)/
      const url = !regex.test(buttonLink) ? '//' + buttonLink : buttonLink
      const linkTargetBlank = targetBlank != null ? targetBlank : true
      window.open(url, linkTargetBlank ? '_blank' : '_top')
    }
  }, [])

  const autoAdvance = (type) => {
    switch (type) {
      case 'MULTIPLE_CHOICE':
        if (formField.settings?.multipleSelection) {
          return false
        }
        return true
      case 'PICTURE_CHOICE':
        if (formField.settings?.multipleSelection) {
          return false
        }
        return true
      case 'DROPDOWN':
        return true
      case 'OPINION':
        return true
      case 'RATING':
        return true
      case 'YES_NO':
        return true
      default:
        return false
    }
  }

  const showSubmit = (answer) => {
    let answerValue = false
    if (answer) {
      switch (answer.type) {
        case 'DATE':
          if (answer.value) {
            const date = JSON.parse(answer.value)
            answerValue =
              Object.keys(date).every((key) => date[key].length > 0) &&
              date.year.length === 4
          }
          break
        default:
          answerValue = answer.value || answer.selection?.length > 0
          break
      }
    }
    if (!subAnswer && autoAdvance(answer?.type)) {
      return false
    }
    return answerValue || !formField.required
  }

  const checkEnding = () => {
    if (fieldType === 'PRODUCT_RECOMMENDATION') {
      return false
    }
    if (fieldType === 'THANK_YOU') {
      return settings?.buttonActive === true
    }
    return true
  }

  const renderSubmitButton = () => {
    if (checkEnding() && showSubmit(answer)) {
      return (
        <SubmitWrapper
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}>
          <FormFieldSubmitButton
            buttonColor={theme.buttonColor}
            borderRadius={theme.borderRadius}
            buttonLink={buttonLink}
            targetBlank={targetBlank}
            autoRedirect={autoRedirect}
            buttonText={buttonText}
            buttonTextColor={theme.buttonTextColor}
            buttonActive={buttonActive}
            font={theme.font}
            valid={valid}
            handleSubmit={handleSubmit}
          />
        </SubmitWrapper>
      )
    }
    return <SubmitPlaceHolder />
  }

  const renderSkipButton = () => {
    if (formField.settings?.showSkip && !formField.required) {
      return (
        <SkipButton
          font={theme.font}
          color={theme.buttonColor}
          handleClick={handleSubmit}
        />
      )
    }
  }

  return (
    <>
      {renderAnswer({
        subId,
        formField,
        theme,
        formFieldOptionIndex,
        setValid,
        valid,
        answer,
        handleAnswer,
        ffShortId,
        shortId,
        draft,
        storeCurrency,
        domain,
        sendIntegrationData,
        sendGaEvent,
        quizName,
        history,
      })}
      {renderSubmitButton()}
      {renderSkipButton()}
    </>
  )
}

Answer.propTypes = {
  formField: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
}

export default Answer
